<template>
  <div class="fair-use" role="dialog" aria-modal="true" aria-labelledby="fair-use-title" @click.self="$emit('close')">
    <div class="fair-use__card">
      <div class="fair-use__head">
        <div>
          <div class="fair-use__eyebrow">Unlimited and Teams plans</div>
          <h2 id="fair-use-title" class="pop-auth__h1 fair-use__title">Fair Use Policy</h2>
        </div>
        <button type="button" class="pop-auth__link fair-use__close" @click="$emit('close')">Close</button>
      </div>
      <p class="pop-auth__sub fair-use__intro">
        Unlimited means unlimited for normal use. To keep the tool fast and fair for everyone, we set an upper limit
        on how many POP page runs a single account can do in a rolling {{ windowDays }}-day window.
      </p>
      <div class="fair-use__tiles">
        <div class="fair-use__tile">
          <div class="fair-use__tile-name">Unlimited</div>
          <div class="fair-use__tile-value">{{ single.pauseLimit }}</div>
          <div class="fair-use__tile-label">page runs per {{ windowDays }} days</div>
        </div>
        <div class="fair-use__tile">
          <div class="fair-use__tile-name">Teams</div>
          <div class="fair-use__tile-value">{{ team.pauseLimit }}</div>
          <div class="fair-use__tile-label">page runs per {{ windowDays }} days</div>
        </div>
      </div>
      <div class="fair-use__subhead">You will hear from us before anything changes</div>
      <div class="fair-use__stages">
        <div v-for="(stage, i) in stages" :key="stage.label" class="fair-use__stage">
          <span class="fair-use__stage-runs" :class="{ 'is-final': i === stages.length - 1 }">{{ stage.runs }} runs</span>
          <span>{{ stage.label }}</span>
        </div>
      </div>
      <div class="fair-use__fine">
        For Teams the same stages apply at double the numbers ({{ teamNumbers }}).
      </div>
      <div class="fair-use__subhead">Good to know</div>
      <ul class="fair-use__list">
        <li>{{ countedRuns }}</li>
        <li>The window is rolling. Runs older than {{ windowDays }} days drop off, and page runs resume on their own once you are back under the limit.</li>
        <li>{{ whilePaused }}</li>
        <li>
          Need more than this on a regular basis?
          <a class="pop-auth__link" href="mailto:support@pageoptimizer.pro?subject=Larger%20POP%20plan" title="Email support@pageoptimizer.pro">Talk to us about a larger plan</a>
          (support@pageoptimizer.pro).
        </li>
      </ul>
      <div class="fair-use__actions">
        <button type="button" class="pop-auth__btn pop-auth__btn--primary fair-use__got-it" @click="$emit('close')">Got it</button>
        <a class="pop-auth__link" href="https://www.pageoptimizer.pro/terms-of-service" target="_blank" rel="noopener">Full terms of service</a>
      </div>
    </div>
  </div>
</template>

<script>
// Numbers come from the public GET /api/fair-use/policy/; the design values are the fallback.
const FALLBACK = {
  windowDays: 90,
  plans: {
    single: { warningThresholds: [200, 300, 400], pauseLimit: 450 },
    team: { warningThresholds: [400, 600, 800], pauseLimit: 900 },
  },
  countedRuns: 'Only POP page runs count. Watchdog reports, API runs and AI Writer, EEAT and NLP credits do not.',
  whilePaused: 'While paused, your account stays active and purchased credits can still be used to run pages.',
}
const STAGE_LABELS = ['First notice by email', 'Second notice', 'Final notice']

export default {
  name: 'FairUseModal',
  data() {
    return { policy: FALLBACK }
  },
  computed: {
    windowDays() {
      return this.policy.windowDays || FALLBACK.windowDays
    },
    single() {
      return (this.policy.plans && this.policy.plans.single) || FALLBACK.plans.single
    },
    team() {
      return (this.policy.plans && this.policy.plans.team) || FALLBACK.plans.team
    },
    stages() {
      const thresholds = this.single.warningThresholds || FALLBACK.plans.single.warningThresholds
      const stages = thresholds.map((runs, i) => ({ runs, label: STAGE_LABELS[i] || 'Notice' }))
      stages.push({ runs: this.single.pauseLimit, label: 'Page runs paused until you are back under the limit' })
      return stages
    },
    teamNumbers() {
      return [...(this.team.warningThresholds || []), this.team.pauseLimit].join(', ')
    },
    // The policy endpoint also carries its own wording, meant for the support pages; the modal
    // keeps the design's shorter copy and only takes the numbers from it.
    countedRuns() {
      return FALLBACK.countedRuns
    },
    whilePaused() {
      return FALLBACK.whilePaused
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onKey)
    this.load()
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKey)
  },
  methods: {
    onKey(event) {
      if (event.key === 'Escape') this.$emit('close')
    },
    async load() {
      try {
        const response = await this.$store.dispatch('fetchFairUsePolicy')
        if (response && response.data && response.data.plans) this.policy = { ...FALLBACK, ...response.data }
      } catch (e) {
        console.log(e)
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import './auth-tokens.scss';

.fair-use {
  position: fixed; inset: 0; z-index: 110;
  background: rgba(1, 71, 81, 0.45); backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center; padding: 20px;

  &__card {
    width: 600px; max-width: 100%; max-height: calc(100vh - 40px); overflow-y: auto;
    background: #fff; color: $pop-teal; border-radius: 18px; padding: 32px;
    box-shadow: 0 24px 60px -24px rgba(1, 71, 81, 0.35);
    text-align: left;
  }
  &__head { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; }
  &__eyebrow { font-size: 12px; font-weight: 700; color: $pop-accent; letter-spacing: 0.06em; text-transform: uppercase; margin-bottom: 8px; }
  &__title { font-size: 24px; text-align: center; }
  &__head > div:first-child { flex: 1; min-width: 0; }
  &__close { color: $pop-ink-muted; font-size: 13px; flex: none; margin-top: 4px; }
  &__intro { text-align: center; margin: 0 auto 18px; max-width: 520px; }
  &__tiles { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; margin-bottom: 20px; }
  &__tile { background: $pop-page-bg; border-radius: 12px; padding: 14px 16px; }
  &__tile-name { font-size: 12.5px; font-weight: 700; color: $pop-ink-muted; }
  &__tile-value { font-family: 'Mulish', sans-serif; font-weight: 800; font-size: 30px; line-height: 1.1; }
  &__tile-label { font-size: 12.5px; }
  &__subhead { font-weight: 800; font-size: 14px; margin-bottom: 8px; }
  &__stages { display: grid; gap: 6px; margin-bottom: 20px; }
  &__stage { display: grid; grid-template-columns: 90px minmax(0, 1fr); gap: 12px; font-size: 13.5px; align-items: baseline; }
  &__stage-runs { font-weight: 800; }
  &__stage-runs.is-final { color: $pop-accent; }
  &__fine { font-size: 12.5px; color: $pop-ink-muted; margin-bottom: 20px; }
  &__list { margin: 0 0 22px; padding-left: 18px; display: grid; gap: 6px; font-size: 13.5px; line-height: 1.5; list-style: disc; }
  &__list li { display: list-item; list-style: disc; }
  &__actions { display: flex; gap: 14px; align-items: center; }
  &__got-it { width: auto; margin-top: 0; font-size: 14px; padding: 12px 22px; }
}
</style>
